<template>
  <div>
    <h2 class="text-2xl font-semibold mb-4">现值盈亏平衡分析</h2>

    <el-card shadow="hover">
      <template #header>
        <div class="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
          <h2 class="text-lg font-semibold">现值盈亏平衡（PV Break-even）</h2>
          <div class="flex items-center gap-2 flex-wrap">
            <el-dropdown class="w-full sm:w-auto" @command="onSelectExample">
              <el-button size="small" plain>示例方案选择</el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-for="ex in examples" :key="ex.key" :command="ex.key">{{ ex.title }}</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-4" title="用途：计算现值盈亏平衡（PV BEP，满足 NPV=0）；保留 NPV(q) 曲线图。">
        <div class="text-sm text-gray-700 mt-2">
          图一（现值直线）：总收入现值 A·[q·p·(1−t)] 与 总成本现值 A·[q·c·(1−t)] + A·[F(1−t) − D·t] + C₀ 的交点 q_E；图二：NPV(q) 曲线，根同为 q_E。
        </div>
      </el-alert>

      <FormulaPanel :title="'核心公式（精简）'" :formulas="[pvBepQFormula, AFormula, C0effFormula]"
        :variables="variablesMap" :collapsible="true" :autoCollapseOnMobile="true" size="compact" />

      <div class="grid grid-cols-1 gap-4">
        <div class="p-4">
          <h3 class="font-medium mb-2">参数配置</h3>
          <el-form :model="form" label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <el-form-item :label="`单价 p（${symbol}）`"><el-input-number v-model="priceUI" :min="0" /></el-form-item>
            <el-form-item :label="`单位变动成本 c（${symbol}）`"><el-input-number v-model="variableCostUI" :min="0" /></el-form-item>
            <el-form-item :label="`年固定成本 F（${symbol}/年）`"><el-input-number v-model="fixedCostUI" :min="0" /></el-form-item>
            <el-form-item :label="`固定资产投资（${symbol}）`"><el-input-number v-model="costUI" :min="0" /></el-form-item>
            <el-form-item label="使用年限（年）"><el-input-number v-model="form.lifeYears" :min="1" /></el-form-item>
            <el-form-item :label="`残值（${symbol}）`"><el-input-number v-model="salvageUI" :min="0" /></el-form-item>
            <el-form-item :label="`期初营运资本（${symbol}）`"><el-input-number v-model="workingCapitalUI" :min="0" /></el-form-item>
            <el-form-item label="所得税率（tax）"><el-input-number v-model="form.taxRate" :min="0" :max="1" :step="0.01" /></el-form-item>
            <el-form-item label="资本成本 r（折现率）"><el-input-number v-model="form.discountRate" :min="0" :max="1" :step="0.01" /></el-form-item>
            <div class="md:col-span-3 flex gap-2 mt-1 items-center"><el-button type="primary" @click="compute">计算</el-button></div>
          </el-form>
        </div>
      </div>

      <!-- 示例弹窗 -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="640px">
        <div class="space-y-3">
          <el-alert type="info" :closable="false" show-icon>
            <template #title>{{ dialogTitle }}（示例）</template>
            <div class="text-sm text-gray-600 leading-relaxed" v-html="dialogDesc"></div>
          </el-alert>
          <el-form label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <el-form-item :label="`单价 p（${symbol}）`"><el-input-number v-model="exPriceUI" :min="0" /></el-form-item>
            <el-form-item :label="`单位变动成本 c（${symbol}）`"><el-input-number v-model="exVariableCostUI" :min="0" /></el-form-item>
            <el-form-item :label="`年固定成本 F（${symbol}/年）`"><el-input-number v-model="exFixedCostUI" :min="0" /></el-form-item>
            <el-form-item :label="`固定资产投资（${symbol}）`"><el-input-number v-model="exCostUI" :min="0" /></el-form-item>
            <el-form-item label="使用年限（年）"><el-input-number v-model="exLifeYearsUI" :min="1" /></el-form-item>
            <el-form-item :label="`残值（${symbol}）`"><el-input-number v-model="exSalvageUI" :min="0" /></el-form-item>
            <el-form-item :label="`期初营运资本（${symbol}）`"><el-input-number v-model="exWorkingCapitalUI" :min="0" /></el-form-item>
            <el-form-item label="所得税率（tax）"><el-input-number v-model="exTaxRateUI" :min="0" :max="1" :step="0.01" /></el-form-item>
            <el-form-item label="资本成本 r（折现率）"><el-input-number v-model="exDiscountRateUI" :min="0" :max="1" :step="0.01" /></el-form-item>
          </el-form>
        </div>
        <template #footer>
          <div class="flex items-center justify-end gap-2"><el-button @click="dialogVisible = false">取消</el-button><el-button type="primary" @click="applyExample">确认并计算</el-button></div>
        </template>
      </el-dialog>

      <el-collapse-transition>
        <div v-show="result" class="p-4">
          <el-card shadow="never" body-class="p-0">
            <div ref="chartEl1" style="height:320px;"></div>
            <div class="text-xs text-gray-500 p-3">图一：红线为总收入现值 PV(TR)，绿色为总成本现值 PV(TC)=PV(VC)+PV(固定成本)+C₀；黑线为变动成本现值 PV(VC)。虚线处为现值盈亏平衡 q_E（与 NPV=0 一致）。</div>
          </el-card>
          <el-card shadow="never" body-class="p-0" class="mt-4">
            <div ref="chartEl2" style="height:320px;"></div>
            <div class="text-xs text-gray-500 p-3">图二：NPV(q) 曲线横轴为销量 q，纵轴为净现值（万元）。蓝点为 q_E，使 NPV(q_E,r)=0（考虑所得税、资本成本、期初投入与期末回收）。</div>
          </el-card>
        </div>
      </el-collapse-transition>

      <div v-if="result" class="mt-4 px-4 pb-4 text-sm text-gray-700 space-y-1">
        <div>A（年金现值系数）= {{ result.A.toFixed(6) }}</div>
        <div>C₀ = Cost + WC − (Salv + WC)/(1+r)^T = {{ formatWan(toDisplay(result.C0eff)) }} 万元</div>
        <div>折旧 D = (Cost − Salvage)/Life = {{ formatMoney(toDisplay(result.depreciation)) }} / 年</div>
        <div>现值盈亏平衡 q_E（NPV=0，r={{ (form.discountRate*100).toFixed(0) }}%，tax={{ (form.taxRate*100).toFixed(0) }}%）= {{ Number.isFinite(result.qEcon) ? result.qEcon.toFixed(0) : '无解' }} 件</div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue'
import * as echarts from 'echarts'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'
import { calculateNPV } from '@/lib/investmentEngine'

const { toDisplay, toBase, symbol, format } = useCurrency()
const formatMoney = (v) => format(v)
const formatWan = (v) => (typeof v === 'number' ? (v/10000).toFixed(2) : v)

const form = ref({
  pricePerUnit: 1800,
  variableCostPerUnit: 800,
  fixedCost: 2000000,
  cost: 30000000,
  lifeYears: 5,
  salvage: 0,
  workingCapital: 15000000,
  taxRate: 0.30,
  discountRate: 0.15
})

const examples = ref([{ key: 'ex_case', title: '示例1（5年期无残值）',
  desc: `固定资产3000万，流动资金1500万；寿命5年；p=1800，c=800，F=200万/年；直线折旧；残值0；r=15%，tax=30%。求现值盈亏平衡点。`,
  form: { pricePerUnit: 1800, variableCostPerUnit: 800, fixedCost: 2000000, cost: 30000000, lifeYears: 5, salvage: 0, workingCapital: 15000000, taxRate: 0.30, discountRate: 0.15 }
}])

const dialogVisible = ref(false)
const dialogTitle = ref('')
const dialogDesc = ref('')
const exForm = ref({})
const onSelectExample = (key) => { const ex = examples.value.find(e => e.key === key); if (!ex) return; dialogTitle.value = ex.title; dialogDesc.value = ex.desc; exForm.value = { ...ex.form }; dialogVisible.value = true }
const applyExample = () => { form.value = { ...exForm.value }; dialogVisible.value = false; compute() }

// 示例弹窗字段
const exPriceUI = computed({ get: () => toDisplay(exForm.value.pricePerUnit || 0), set: v => exForm.value.pricePerUnit = toBase(v) })
const exVariableCostUI = computed({ get: () => toDisplay(exForm.value.variableCostPerUnit || 0), set: v => exForm.value.variableCostPerUnit = toBase(v) })
const exFixedCostUI = computed({ get: () => toDisplay(exForm.value.fixedCost || 0), set: v => exForm.value.fixedCost = toBase(v) })
const exCostUI = computed({ get: () => toDisplay(exForm.value.cost || 0), set: v => exForm.value.cost = toBase(v) })
const exLifeYearsUI = computed({ get: () => exForm.value.lifeYears || 0, set: v => exForm.value.lifeYears = Number(v) })
const exSalvageUI = computed({ get: () => toDisplay(exForm.value.salvage || 0), set: v => exForm.value.salvage = toBase(v) })
const exWorkingCapitalUI = computed({ get: () => toDisplay(exForm.value.workingCapital || 0), set: v => exForm.value.workingCapital = toBase(v) })
const exTaxRateUI = computed({ get: () => exForm.value.taxRate ?? 0, set: v => exForm.value.taxRate = Number(v) })
const exDiscountRateUI = computed({ get: () => exForm.value.discountRate ?? 0, set: v => exForm.value.discountRate = Number(v) })

// 主表单字段
const priceUI = computed({ get: () => toDisplay(form.value.pricePerUnit || 0), set: v => form.value.pricePerUnit = toBase(v) })
const variableCostUI = computed({ get: () => toDisplay(form.value.variableCostPerUnit || 0), set: v => form.value.variableCostPerUnit = toBase(v) })
const fixedCostUI = computed({ get: () => toDisplay(form.value.fixedCost || 0), set: v => form.value.fixedCost = toBase(v) })
const costUI = computed({ get: () => toDisplay(form.value.cost || 0), set: v => form.value.cost = toBase(v) })
const salvageUI = computed({ get: () => toDisplay(form.value.salvage || 0), set: v => form.value.salvage = toBase(v) })
const workingCapitalUI = computed({ get: () => toDisplay(form.value.workingCapital || 0), set: v => form.value.workingCapital = toBase(v) })

const result = ref(null)
const chartEl1 = ref(null)
const chartEl2 = ref(null)
let chart1 = null
let chart2 = null

function compute() {
  const p = Number(form.value.pricePerUnit) || 0
  const c = Number(form.value.variableCostPerUnit) || 0
  const F = Number(form.value.fixedCost) || 0
  const Cost = Number(form.value.cost) || 0
  const Salv = Number(form.value.salvage || 0)
  const WC = Number(form.value.workingCapital || 0)
  const T = Math.max(1, Number(form.value.lifeYears) || 1)
  const r = Number(form.value.discountRate) || 0
  const tax = Number(form.value.taxRate) || 0

  const D = (Cost - Salv) / T

  // NPV(q) 根（数值法，用于稳健）
  const ocfForQ = (q) => {
    const Rev = q * p
    const Var = q * c
    const EBIT = Rev - Var - F - D
    const Tax = Math.max(0, EBIT * tax)
    return (EBIT - Tax) + D
  }
  const cashFlowsForQ = (q) => {
    const flows = [-(Cost + WC)]
    const ocf = ocfForQ(q)
    for (let i = 1; i <= T; i++) flows.push(i === T ? ocf + Salv + WC : ocf)
    return flows
  }
  const npvForQ = (q) => calculateNPV(cashFlowsForQ(q), r)
  let qE = NaN
  if (npvForQ(0) >= 0) qE = 0
  else {
    let lo = 0, hi = 1, iter = 0
    while (npvForQ(hi) < 0 && iter++ < 40) hi *= 2
    if (npvForQ(hi) >= 0) {
      for (let k = 0; k < 60; k++) {
        const mid = (lo + hi) / 2
        const f = npvForQ(mid)
        if (Math.abs(f) < 1e-2 || (hi - lo) < 1e-3) { qE = mid; break }
        if (f >= 0) hi = mid; else lo = mid
      }
      if (!Number.isFinite(qE)) qE = (lo + hi) / 2
    }
  }

  // 现值封闭解（与上式应一致）
  const A = r > 0 ? (1 - Math.pow(1 + r, -T)) / r : T
  const C0eff = (Cost + WC) - (Salv + WC) / Math.pow(1 + r, T)
  const denom = A * (p - c) * (1 - tax)
  const numer = A * (F * (1 - tax) - D * tax) + C0eff
  const qE_pv = denom > 0 ? numer / denom : NaN

  result.value = { A, C0eff, depreciation: D, qEcon: qE, qEpv: qE_pv }
  nextTick(() => { renderChart1(); renderChart2() })
}

function renderChart1() {
  if (!result.value || !chartEl1.value) return
  if (!chart1) chart1 = echarts.init(chartEl1.value)
  const p = Number(form.value.pricePerUnit) || 0
  const c = Number(form.value.variableCostPerUnit) || 0
  const F = Number(form.value.fixedCost) || 0
  const Cost = Number(form.value.cost) || 0
  const Salv = Number(form.value.salvage || 0)
  const WC = Number(form.value.workingCapital || 0)
  const T = Math.max(1, Number(form.value.lifeYears) || 1)
  const r = Number(form.value.discountRate) || 0
  const tax = Number(form.value.taxRate) || 0
  const D = (Cost - Salv) / T
  const A = r > 0 ? (1 - Math.pow(1 + r, -T)) / r : T
  const C0eff = (Cost + WC) - (Salv + WC) / Math.pow(1 + r, T)
  const qE = result.value.qEcon

  const qMax = Number.isFinite(qE) && qE > 0 ? Math.ceil(qE * 1.6) : 100
  const qArr = []
  for (let q = 0; q <= qMax; q += Math.max(1, Math.floor(qMax/40))) qArr.push(q)
  const toWan = (v) => (typeof v === 'number' ? Number((toDisplay(v) / 10000).toFixed(2)) : 0)

  const pvTR = qArr.map(q => toWan(A * (1 - tax) * p * q))
  const pvVC = qArr.map(q => toWan(A * (1 - tax) * c * q))
  const intercept = A * (F * (1 - tax) - D * tax) + C0eff
  const pvTC = qArr.map(q => toWan(A * (1 - tax) * c * q + intercept))

  chart1.setOption({
    tooltip: { trigger: 'axis', valueFormatter: (v) => (v == null ? '' : `${v} 万元（现值）`) },
    legend: { data: ['总收入现值 PV(TR)', '总成本现值 PV(TC)', '变动成本现值 PV(VC)'] },
    xAxis: { type: 'category', name: '销量 q（件）', data: qArr },
    yAxis: { type: 'value', name: '金额（万元，现值）' },
    series: [
      { name: '总收入现值 PV(TR)', type: 'line', data: pvTR, lineStyle: { color: '#ef4444' },
        markLine: { symbol: 'none', lineStyle: { type: 'dashed', color: '#94a3b8' }, data: [ ...(Number.isFinite(qE) && qE > 0 ? [{ xAxis: qE, label: { formatter: 'q_E' } }] : []) ] }
      },
      { name: '总成本现值 PV(TC)', type: 'line', data: pvTC, lineStyle: { color: '#10b981' } },
      { name: '变动成本现值 PV(VC)', type: 'line', data: pvVC, lineStyle: { color: '#111827' } },
      ...(Number.isFinite(qE) && qE > 0 ? [{ name: '现值盈亏平衡', type: 'scatter', data: [[qE, toWan(A * (1 - tax) * p * qE)]], symbolSize: 10, itemStyle: { color: '#3b82f6' } }] : [])
    ],
    grid: { left: 60, right: 20, top: 40, bottom: 50 }
  })
}

function renderChart2() {
  if (!result.value || !chartEl2.value) return
  if (!chart2) chart2 = echarts.init(chartEl2.value)
  const p = Number(form.value.pricePerUnit) || 0
  const c = Number(form.value.variableCostPerUnit) || 0
  const F = Number(form.value.fixedCost) || 0
  const Cost = Number(form.value.cost) || 0
  const Salv = Number(form.value.salvage || 0)
  const WC = Number(form.value.workingCapital || 0)
  const T = Math.max(1, Number(form.value.lifeYears) || 1)
  const r = Number(form.value.discountRate) || 0
  const tax = Number(form.value.taxRate) || 0
  const D = (Cost - Salv) / T
  const qE = result.value.qEcon

  const ocfForQ = (q) => {
    const Rev = q * p
    const Var = q * c
    const EBIT = Rev - Var - F - D
    const Tax = Math.max(0, EBIT * tax)
    return (EBIT - Tax) + D
  }
  const cashFlowsForQ = (q) => {
    const flows = [-(Cost + WC)]
    const ocf = ocfForQ(q)
    for (let i = 1; i <= T; i++) flows.push(i === T ? ocf + Salv + WC : ocf)
    return flows
  }
  const toWan = (v) => (typeof v === 'number' ? Number((toDisplay(v) / 10000).toFixed(2)) : 0)

  const qMax = Number.isFinite(qE) && qE > 0 ? Math.ceil(qE * 1.6) : 100
  const qArr = []
  for (let q = 0; q <= qMax; q += Math.max(1, Math.floor(qMax/40))) qArr.push(q)
  const npvData = qArr.map(q => toWan(calculateNPV(cashFlowsForQ(q), r)))

  chart2.setOption({
    tooltip: { trigger: 'axis', valueFormatter: (v) => (v == null ? '' : `${v} 万元`) },
    legend: { data: ['NPV(q)'] },
    xAxis: { type: 'category', name: '销量 q（件）', data: qArr },
    yAxis: { type: 'value', name: 'NPV（万元）' },
    series: [
      { name: 'NPV(q)', type: 'line', data: npvData, lineStyle: { color: '#0ea5e9' } },
      ...(Number.isFinite(qE) && qE >= 0 ? [{ name: 'NPV=0', type: 'scatter', data: [[qE, 0]], symbolSize: 10, itemStyle: { color: '#3b82f6' } }] : [])
    ],
    grid: { left: 60, right: 20, top: 40, bottom: 50 }
  })
}

// 公式与变量（精简，仅保留核心三式）
const pvBepQFormula = String.raw`q_E = \dfrac{A\,[F(1-t) - D\,t] + C_0}{A\,(p-c)\,(1-t)}`
const AFormula = String.raw`A = \dfrac{1-(1+r)^{-T}}{r}`
const C0effFormula = String.raw`C_0 = Cost + WC - \dfrac{Salv + WC}{(1+r)^T}`

const variablesMap = [
  { symbol: 'q', label: '销量（件）' },
  { symbol: 'p', label: '单位销售价格' },
  { symbol: 'c', label: '单位变动成本' },
  { symbol: 'F', label: '年固定成本（不含折旧）' },
  { symbol: 'D', label: '年折旧（直线法）' },
  { symbol: 'r', label: '资本成本（折现率）' },
  { symbol: 't', label: '所得税率（tax）' },
  { symbol: 'A', label: '年金现值系数' },
  { symbol: 'C_0', label: '期初净支出（含营运资本−期末回收现值）' },
  { symbol: 'WC', label: '期初营运资本（期末回收）' },
  { symbol: 'Salv', label: '期末残值' }
]
</script>

<style scoped>
</style>
        
